import { createRouter, createWebHistory } from "vue-router"
import { t } from "@/plugins/i18n/i18n"
import LoadingView from "@/features/LoadingView.vue"
import TransitionView from "@/features/TransitionView.vue"
import { usePreviousStateModalStore } from "@/components/af/PreviousStateModal/previousStateModal.store"
import { useLeaveAppModalStore } from "@/components/af/LeaveAppModal/leaveAppModal.store"
import { storeToRefs } from "pinia"
import {
  MixpanelInstance,
  isMixpanelInitialized,
} from "@/plugins/mixpanel/mixpanel"
import { useWindowScroll } from "@vueuse/core"
import ContactSupportView from "@/features/ContactSupportView.vue"
import MaintenanceView from "@/features/MaintenanceView.vue"
import { useUserStore } from "@/stores/user"
import { isScreeningErrorConnectionPreview } from "@/features/screening/screeningErrorConnectionPreview"

// Flag telling whether the navigation currently being resolved comes from a real
// browser back/forward. It MUST be registered before createWebHistory() below:
// vue-router adds its own popstate listener there and runs the guards synchronously
// from it, so a listener registered later would fire too late to be of any use.
let isBrowserBackForward = false
window.addEventListener("popstate", () => {
  isBrowserBackForward = true
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "start",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: LoadingView,
    },
    // use two root routes to handle the first navigation and push history state
    {
      path: "/loading",
      name: "loading",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: LoadingView,
    },
    {
      path: "/transition",
      name: "transition",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: TransitionView,
    },
    // Outside production only, reached with /?goto=screening-error-connection
    // (see isScreeningErrorConnectionPreview): previews the Scan connection/VPN error page
    {
      path: "/screening-error-connection",
      name: "screeningErrorConnectionPreview",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: () =>
        import("@/components/af/Screening/ScreeningErrorCard.vue"),
      props: { source: "connection" },
    },
    {
      path: "/maintenance",
      name: "maintenance",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: MaintenanceView,
    },
    {
      path: "/contact-support",
      name: "contactSupport",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: ContactSupportView,
    },
    {
      path: "/steps",
      name: "stepper",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: () => import("@/features/stepper/StepperView.vue"),
    },
    {
      // Shape has been replaced by Swipe: keep legacy /shape URLs working
      path: "/shape/:pathMatch(.*)*",
      redirect: { name: "swipe" },
    },
    {
      path: "/swipe",
      name: "swipe",
      meta: { title: "swipe.meta.title" },
      component: () => import("@/features/swipe/Swipe.vue"),
      // redirect to swipe intro is handled in the swipeMachine
      children: [
        {
          path: "loading",
          name: "swipeLoading",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeLoading.vue"),
        },
        {
          path: "trial",
          name: "swipeTrial",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeTrial.vue"),
        },
        {
          path: "test",
          name: "swipeTest",
          meta: { title: "swipe.meta.title" },
          component: () => import("@/features/swipe/views/SwipeTest.vue"),
        },
        {
          path: "load-results",
          name: "swipeLoadResults",
          meta: { title: "swipe.meta.title" },
          component: () =>
            import("@/features/swipe/views/SwipeLoadResults.vue"),
        },
      ],
    },
    {
      path: "/drive",
      name: "drive",
      meta: { title: "drive.meta.title" },
      component: () => import("@/features/drive/Drive.vue"),
      // redirect to drive intro is handled in the driveMachine
      children: [
        {
          path: "loading",
          name: "driveLoading",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveLoading.vue"),
        },
        {
          path: "tutorial",
          name: "driveTuto",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveTuto.vue"),
        },
        {
          path: "test",
          name: "driveTest",
          meta: { title: "drive.meta.title" },
          component: () => import("@/features/drive/views/DriveTest.vue"),
        },
        {
          path: "load-results",
          name: "driveLoadResults",
          meta: { title: "drive.meta.title" },
          component: () =>
            import("@/features/drive/views/DriveLoadResults.vue"),
        },
      ],
    },
    {
      path: "/brain",
      name: "brain",
      meta: { title: "brain.meta.title" },
      component: () => import("@/features/brain/Brain.vue"),
      // redirect to the right Brain step is handled in the brainMachine
      children: [
        {
          path: "loading",
          name: "brainLoading",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainLoading.vue"),
        },
        {
          path: "tutorial",
          name: "brainTuto",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainTuto.vue"),
        },
        {
          path: "resume",
          name: "brainResume",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainResume.vue"),
        },
        {
          path: "test",
          name: "brainTest",
          meta: { title: "brain.meta.title" },
          component: () => import("@/features/brain/views/BrainTest.vue"),
        },
        {
          path: "load-results",
          name: "brainLoadResults",
          meta: { title: "brain.meta.title" },
          component: () =>
            import("@/features/brain/views/BrainLoadResults.vue"),
        },
      ],
    },
    {
      path: "/voice",
      name: "voice",
      meta: { title: "voice.meta.title" },
      component: () => import("@/features/voice/Voice.vue"),
      // redirect to the first voice step is handled in the voiceMachine
      children: [
        {
          path: "loading",
          name: "voiceLoading",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceLoading.vue"),
        },
        {
          path: "welcome-back",
          name: "voiceWelcomeBack",
          meta: { title: "voice.meta.title" },
          component: () =>
            import("@/features/voice/views/VoiceWelcomeBack.vue"),
        },
        {
          path: "authorize",
          name: "voiceAuthorize",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceAuthorize.vue"),
        },
        {
          path: "authorization-refused",
          name: "voiceAuthorizeRefused",
          meta: { title: "voice.meta.title" },
          component: () =>
            import("@/features/voice/views/VoiceAuthorizeRefused.vue"),
        },
        {
          path: "tutorial",
          name: "voiceTuto",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceTuto.vue"),
        },
        {
          path: "test",
          name: "voiceTest",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceTest.vue"),
        },
        {
          path: "feedback",
          name: "voiceFeedback",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceFeedback.vue"),
        },
        {
          path: "end",
          name: "voiceEnd",
          meta: { title: "voice.meta.title" },
          component: () => import("@/features/voice/views/VoiceEnd.vue"),
        },
        {
          path: "load-results",
          name: "voiceLoadResults",
          meta: { title: "voice.meta.title" },
          component: () =>
            import("@/features/voice/views/VoiceLoadResults.vue"),
        },
      ],
    },
    {
      path: "/screening",
      name: "screening",
      meta: { title: "scan.meta.title" },
      component: () => import("@/features/screening/Screening.vue"),
      // redirect to drive intro is handled in the driveMachine
      children: [
        {
          path: "initial-loading",
          name: "screeningLoading",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/components/af/Screening/ScreeningLoading.vue"),
        },
        {
          path: "intro",
          name: "screeningIntro",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningIntro.vue"),
        },
        {
          path: "authorize",
          name: "screeningAuthorize",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningAuthorize.vue"),
        },
        {
          path: "loading",
          name: "screeningRoomLoading",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/room/ScreeningRoomLoader.vue"),
        },
        {
          path: "error",
          name: "screeningError",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningError.vue"),
        },
        {
          path: "test",
          name: "screeningTest",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/room/ScreeningRoomAgent.vue"),
        },
        {
          path: "review",
          name: "screeningReview",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningReview.vue"),
        },
        {
          path: "load-results",
          name: "screeningLoadResults",
          meta: { title: "scan.meta.title" },
          component: () =>
            import("@/features/screening/views/ScreeningLoadResults.vue"),
        },
      ],
    },
    {
      path: "/screening-cv",
      name: "screeningCv",
      meta: { title: "register_guest.meta.title" },
      component: () => import("@/features/screening-cv/ScreeningCv.vue"),
      children: [
        {
          path: "loading",
          name: "screeningCvLoading",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/components/af/Screening/ScreeningLoading.vue"),
        },
        {
          path: "intro",
          name: "screeningCvIntro",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvIntro.vue"),
        },
        {
          path: "form",
          name: "screeningCvForm",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvUploadForm.vue"),
        },
        {
          path: "load-results",
          name: "screeningCvLoadResults",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvLoadResults.vue"),
        },
        {
          path: "error",
          name: "screeningCvError",
          meta: { title: "register_guest.meta.title" },
          component: () =>
            import("@/features/screening-cv/views/ScreeningCvError.vue"),
        },
      ],
    },
    {
      path: "/meeting",
      name: "meeting",
      meta: { title: "scan.meta.book_meeting" },
      component: () => import("@/features/book-meeting/BookMeeting.vue"),
    },
    {
      path: "/move",
      name: "move",
      meta: { title: "move.meta.title" },
      component: () => import("@/features/move/Move.vue"),
      // redirect is handled in the moveMachine
      children: [
        {
          path: "loading",
          name: "moveLoading",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveLoading.vue"),
        },
        {
          path: "tutorial",
          name: "moveTuto",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveTuto.vue"),
        },
        {
          path: "test",
          name: "moveTest",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveTest.vue"),
        },
        {
          path: "load-results",
          name: "moveLoadResults",
          meta: { title: "move.meta.title" },
          component: () => import("@/features/move/views/MoveLoadResults.vue"),
        },
      ],
    },
    {
      path: "/movebis",
      name: "movebis",
      meta: { title: "movebis.meta.title" },
      component: () => import("@/features/movebis/MoveBis.vue"),
      // redirect is handled in the moveBisMachine
      children: [
        {
          path: "loading",
          name: "moveBisLoading",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisLoading.vue"),
        },
        {
          path: "tutorial",
          name: "moveBisTuto",
          meta: { title: "movebis.meta.title" },
          component: () => import("@/features/movebis/views/MoveBisTuto.vue"),
        },
        {
          path: "test",
          name: "moveBisTest",
          meta: { title: "movebis.meta.title" },
          component: () => import("@/features/movebis/views/MoveBisTest.vue"),
        },
        {
          path: "load-results",
          name: "moveBisLoadResults",
          meta: { title: "movebis.meta.title" },
          component: () =>
            import("@/features/movebis/views/MoveBisLoadResults.vue"),
        },
      ],
    },
    {
      path: "/thank-you",
      name: "thanksATS",
      meta: { title: "candidate_account.manage_process.meta.title" },
      component: () => import("@/features/thanksATSView.vue"),
    },
  ],
})

// this guard is to :
// - if firstNavigation : handle direct access to a page, before machine is started
// - otherwise : trap the browser back button so the user cannot leave the flow by
//   going back. Instead we cancel the navigation and show the leave/previous-state
//   modal (whose "Leave" button performs the real exit).
//
// The two root routes "start" (/) and "loading" (/loading) both render LoadingView
// and sit in history underneath the flow. Going back from the stepper / a test lands
// on one of them, which would let the user escape the flow: we trap that, cancel the
// navigation and show the modal instead. Only a real back/forward is trapped, so the
// machine's own navigations (boot: start -> loading -> stepper) are left alone.
// vue-router 5: `return false` cancels the navigation, `return "/"` redirects.

let isFirstNavigation = true
router.beforeEach(async (to, from) => {
  const { y } = useWindowScroll({ behavior: "smooth" })
  if (y.value > 0) y.value = 0

  if (isFirstNavigation) {
    isFirstNavigation = false

    const userStore = useUserStore()
    // L'échec est déjà traité par l'intercepteur axios (redirection login, ou
    // modale de sortie d'iframe). Sans ce catch, la promesse rejetée avorte la
    // navigation et le RouterView ne rend jamais rien.
    try {
      await userStore.fetchUser()
    } catch {
      // noop
    }

    // Redirect here and not from the global machine: this redirect would
    // otherwise land after the machine's navigation and override it
    if (isScreeningErrorConnectionPreview()) {
      return { name: "screeningErrorConnectionPreview" }
    }

    return "/"
  }

  // consume the flag for this navigation
  const cameFromBackButton = isBrowserBackForward
  isBrowserBackForward = false

  const testDirectories = [
    "swipe",
    "drive",
    "brain",
    "voice",
    "move",
    "movebis",
    "screening",
    "screeningCv",
    "meeting",
  ]
  const mainDirectories = ["stepper", "loading"]

  const isTestDirectory =
    from.matched.length > 0 &&
    testDirectories.includes(from.matched[0].name?.toString() || "")
  const isMainDirectory =
    from.matched.length > 0 &&
    mainDirectories.includes(from.matched[0].name?.toString() || "")

  const isLeavingToRoot = to.name === "start" || to.name === "loading"

  // Trap the back button: cancel the navigation, restore the URL, show the modal.
  if (
    isLeavingToRoot &&
    (isTestDirectory || isMainDirectory) &&
    cameFromBackButton
  ) {
    // NB: no manual history.pushState here — `return false` below already cancels the
    // popstate and restores the URL. Doing both corrupts the history stack and the
    // next back press then resolves to the same route (no navigation, no modal).
    if (isTestDirectory) {
      const { isActive } = storeToRefs(usePreviousStateModalStore())
      isActive.value = true // Display the previous state modal

      isMixpanelInitialized() &&
        MixpanelInstance.track("modal_viewed", {
          name: "previous_state_modal",
        })
    } else {
      const { isActive } = storeToRefs(useLeaveAppModalStore())
      isActive.value = true // Display the leave app modal
    }
    return false // cancel -> stay in the app
  }

  // otherwise, let the navigation happen
  return true
})

// name of page visited before loading page
type RouteRecordName = string | symbol | undefined | null
let penultimatePage: RouteRecordName = ""
const pagesToIgnore: RouteRecordName[] = [
  "loading",
  "driveLoading",
  "brainLoading",
  "moveLoading",
  "movebisLoading",
  "screeningLoading",
  "screeningCvLoading",
  "stepper",
]

// Try to fix : Error on imported module
// Source : https://stackoverflow.com/questions/69300341/typeerror-failed-to-fetch-dynamically-imported-module-on-vue-vite-vanilla-set
router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  ) {
    window.location.href = to.fullPath
  }
})

router.afterEach((to, from) => {
  // const mixpanel = inject("mixpanel") as CustomMixpanel
  document.title =
    to.name === "start"
      ? "Assessment onboarding | AssessFirst"
      : t(to.meta.title as string)

  // dans l'idéal on devrait envoyer un event à la machine global
  // mais je ne parviens à réinitialiser la swipeMachine, du coup ça pète le flow XState
  // donc plan B: pour l'instant on reload la page
  // globalActor.send({ type: "GLOBAL.GOTO.STEPPER" })

  // Reset XState by reloading the page ONLY when coming back to the stepper from an
  // actual test. Never reload when arriving from a root/transient route ("start" or
  // "loading"): that's the boot path (start -> stepper), and reloading there loops.
  if (
    to.name === "stepper" &&
    from.name !== "loading" &&
    from.name !== "start" &&
    from.matched[0]?.name !== "screening"
  ) {
    history.pushState(null, "", from.fullPath) // remove last entry from history to disable previous button in the browser
    window.location.reload()
  }

  if (
    from.name &&
    !pagesToIgnore.includes(from.name) &&
    isMixpanelInitialized()
  ) {
    MixpanelInstance.track(`Leave ${from.name.toString()}`)
  }

  // start new time reporter, and enter log
  if (to.name && !pagesToIgnore.includes(to.name) && isMixpanelInitialized()) {
    MixpanelInstance.time_event(`Leave ${to.name.toString()}`)
    MixpanelInstance.track(`Enter ${to.name.toString()}`, {
      entry_point: penultimatePage !== "" ? penultimatePage : from.name,
    })
  }

  if (pagesToIgnore.includes(to.name)) {
    penultimatePage = from.name || ""
  } else {
    penultimatePage = "" // reset penultimatePage
  }
})

export default router
